// src/context/UserProvider.js
/* eslint-disable unicode-bom */
import React from "react";
import { app, __FIREBASE_CONFIG_OK__ } from "../lib/firebase";
import { getAuth, onAuthStateChanged, onIdTokenChanged, signOut } from "firebase/auth";
import {
  getFirestore,
  doc,
  getDoc,
  onSnapshot,
  updateDoc,
  setDoc,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getLocalDeviceId } from "../utils/device";
import { getCountryFromIP } from "../utils/ipAddress";

const WEB_COOLDOWN_MS = 48 * 60 * 60 * 1000; // 48h
const DISABLE_REALTIME = String(process.env.REACT_APP_DISABLE_REALTIME || "").trim() === "1";

// Internal flags/timers to downgrade from realtime to polling when blocked by client
const realtimeDisabledRef = { current: DISABLE_REALTIME };

function normalizeCurrencyCode(value) {
  const code = String(value || "USD").trim().toUpperCase();
  return code === "BGN" ? "EUR" : code || "USD";
}

/* ----------------------------------------------------------------------------
   Cache helpers for getUserDiscount
---------------------------------------------------------------------------- */
const DISCOUNT_CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6 hours
const CACHE_PREFIX = "discRules:v2:";

function makeCacheKey(uid, payload) {
  const { role, discount, country, method } = payload || {};
  return `${CACHE_PREFIX}${uid}:${role}|${discount}|${country}|${method}`;
}
function readCache(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.value || !parsed.expiresAt) return null;
    if (Date.now() > parsed.expiresAt) return null;
    return parsed.value;
  } catch {
    return null;
  }
}
function writeCache(key, value, ttlMs = DISCOUNT_CACHE_TTL_MS) {
  try {
    localStorage.setItem(
      key,
      JSON.stringify({ value, expiresAt: Date.now() + ttlMs })
    );
  } catch {
    /* ignore quota errors */
  }
}

/* ----------------------------------------------------------------------------
   Signing date helpers
---------------------------------------------------------------------------- */
function toDateString(timestamp) {
  const date = new Date(timestamp);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day}, ${hours}:${minutes}:${seconds}`;
}

async function saveSigningDate(db, uid) {
  try {
    const usersDataRef = doc(db, "usersData", uid);
    const signingDate = toDateString(Date.now());
    await updateDoc(usersDataRef, { signingDate });
  } catch (error) {
    // Silent fail - document may not exist or permissions issue
  }
}

/* ----------------------------------------------------------------------------
   Pricing helpers (tier selection by USD buckets)
---------------------------------------------------------------------------- */
function percentForPrice(rulesObj, priceUSD) {
  if (!rulesObj || typeof rulesObj !== "object" || typeof priceUSD !== "number")
    return 0;
  if (priceUSD <= 50) return Number(rulesObj['0-50']) || 0;
  if (priceUSD > 50 && priceUSD <= 100) return Number(rulesObj['51-100']) || 0;
  if (priceUSD > 100 && priceUSD <= 200) return Number(rulesObj['101-200']) || 0;
  if (priceUSD > 200 && priceUSD <= 350) return Number(rulesObj['201-350']) || 0;
  if (priceUSD > 350 && priceUSD <= 500) return Number(rulesObj['351-500']) || 0;
  if (priceUSD > 500) return Number(rulesObj['501+']) || 0;
  return 0;
}

/* ----------------------------------------------------------------------------
   Role sorting helpers
---------------------------------------------------------------------------- */
function getRoleSortPriority(label) {
  const normalizedLabel = String(label || "").toLowerCase().replace(/\s+/g, " ").trim();

  // Fixed priority roles
  const priorityMap = {
    'admin': 1,
    'manager plus': 2,
    'manager': 3,
  };

  // Check for fixed priority roles first
  if (priorityMap[normalizedLabel]) {
    return priorityMap[normalizedLabel];
  }

  // Handle Gold rankings (Gold 20 = 4, Gold 19 = 5, ..., Gold 1 = 23)
  const goldMatch = normalizedLabel.match(/^gold\s*(\d+)$/);
  if (goldMatch) {
    const goldNumber = parseInt(goldMatch[1], 10);
    // Gold 20 should be priority 4, Gold 1 should be priority 23
    // So: priority = 24 - goldNumber
    return 24 - goldNumber;
  }

  // Handle "gold" without number as Gold 1
  if (normalizedLabel === 'gold') {
    return 23; // Same as Gold 1
  }

  // Premium comes after all Gold levels
  if (normalizedLabel === 'premium') {
    return 24;
  }

  // Unknown roles go to end
  return 999;
}

function sortRoleOptions(roleOptions) {
  return [...roleOptions].sort((a, b) => {
    const priorityA = getRoleSortPriority(a.label);
    const priorityB = getRoleSortPriority(b.label);

    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    // If same priority, sort alphabetically
    return a.label.localeCompare(b.label);
  });
}

/* ----------------------------------------------------------------------------
   Context
---------------------------------------------------------------------------- */
const defaultSettings = {
  firstName: "",
  lastName: "",
  email: "",
  phoneNumber: "",
  currency: "USD",
  discount: "free",         // user-selected role
  personalDiscount: 0,
  country: "",
  resetDiscount: false,
  selectedList: "default",
  lists: {},
  selectedListBoard: "default",
  listsBoards: {},
  canScreenshot: false,    // Screenshot permission from Firestore
};

export const UserContext = React.createContext({
  ready: false,
  uid: null,
  emailVerified: false,
  needsProfile: false,
  needsEmailVerification: false,
  claims: { admin: false, managerPlus: false, manager: false },
  basePrivRole: "free",
  settings: defaultSettings,
  roleOptions: [],          // [{code,label}]
  gates: { navigation: {}, details: {}, features: {} },
  subscription: { method: "None", valid: false, platform: null, startDate: 0, endDate: 0 },
  discountRules: {},        // role rules from getUserDiscount
  pricing: { computePriceUSD: (usd) => usd, explain: (usd) => ({ rolePct: 0, personalPct: 0 }) },
  refreshUserSettings: async () => { },
  createProfile: async () => { },
  resendEmailVerification: async () => { },
  refreshUser: async () => { },
  signOut: async () => { },
  forceSignIn: async () => { },
  deviceLock: { locked: false, until: 0, needsConfirmation: false },
});
export const useUser = () => React.useContext(UserContext);

/* ----------------------------------------------------------------------------
   Provider
---------------------------------------------------------------------------- */
export function UserProvider({ children }) {
  const hasFirebase = !!app && __FIREBASE_CONFIG_OK__;
  const auth = React.useMemo(() => (hasFirebase ? getAuth(app) : null), [hasFirebase]);
  const db = React.useMemo(() => (hasFirebase ? getFirestore(app) : null), [hasFirebase]);
  const functions = React.useMemo(() => (hasFirebase ? getFunctions(app) : null), [hasFirebase]);
  const lastRulesKeyRef = React.useRef(null);
  const settingsRef = React.useRef(defaultSettings);
  const resetAppliedRef = React.useRef(false);
  const baseRoleRef = React.useRef("free");
  const lastSubStatusRef = React.useRef({ active: false, startDate: 0, endDate: 0, method: "None", initialized: false });
  const wasOwnerRef = React.useRef(false);

  const [state, setState] = React.useState({
    ready: false,
    uid: null,
    emailVerified: false,
    needsProfile: false,
    needsEmailVerification: false,
    claims: { admin: false, managerPlus: false, manager: false },
    basePrivRole: "free",
    settings: defaultSettings,
    roleOptions: [],
    roleListAvailable: false,
    gates: { navigation: {}, details: {}, features: {} },
    subscription: { method: "None", valid: false, platform: null, startDate: 0, endDate: 0 },
    discountRules: {},
    deviceLock: null,
  });

  /* ------------------------------ Auth wiring ------------------------------ */
  React.useEffect(() => {
    if (!hasFirebase) {
      setState((s) => ({
        ...s,
        ready: true,
        uid: null,
      }));
      return;
    }
    let unsubUserDoc = null;
    let unsubSubDoc = null;
    let userPollTimer = null;
    let subPollTimer = null;

    async function fetchUserOnce(userRef, user, basePrivRole) {
      try {
        const snap = await getDoc(userRef);
        if (!snap.exists()) {
          const needsEmailVerification = !!user.email && !user.emailVerified;
          setState((prev) => ({
            ...prev,
            uid: user.uid,
            emailVerified: !!user.email && user.emailVerified,
            needsEmailVerification,
            needsProfile: true,
            deviceLock: { locked: false, until: 0 },
            settings: defaultSettings,
            ready: true,
          }));
          return;
        }
        const d = snap.data() || {};
        const localId = getLocalDeviceId();
        const serverId = d.uniqueId || null;
        const lastUpdateRaw = d.uniqueIdUpdateDate || 0;
        const lastUpdate = typeof lastUpdateRaw === 'number' ? lastUpdateRaw : (Date.parse(String(lastUpdateRaw)) || 0);
        const lockUntil = lastUpdate > 0 ? (lastUpdate + WEB_COOLDOWN_MS) : 0;
        const privileged = ["admin", "managerPlus"].includes(basePrivRole);
        let deviceLock = { locked: false, until: 0, needsConfirmation: false };
        if (!privileged && serverId && serverId !== localId) {
          if (Date.now() < lockUntil) {
            // Cooldown active - show device lock screen
            deviceLock = { locked: true, until: lockUntil, needsConfirmation: false };
          } else {
            // Cooldown expired - show force sign-in warning
            deviceLock = { locked: false, until: 0, needsConfirmation: true };
          }
        }
        const settings = {
          firstName: d.firstName || "",
          lastName: d.lastName || "",
          email: d.email || user.email || "",
          phoneNumber: d.phoneNumber || "",
          currency: normalizeCurrencyCode(d.currency),
          discount: d.discount || "free",
          personalDiscount: typeof d.personalDiscount === "number" ? d.personalDiscount : 0,
          country: d.country || "",
          resetDiscount: !!d.resetDiscount,
          selectedList: d.selectedList || "default",
          lists: d.lists || {},
          selectedListBoard: d.selectedListBoard || "default",
          listsBoards: d.listsBoards || {},
          canScreenshot: d.canScreenshot === true,
        };
        settingsRef.current = settings;
        const needsEmailVerification = !!user.email && !user.emailVerified;
        setState((prev) => ({
          ...prev,
          uid: user.uid,
          emailVerified: !!user.email && user.emailVerified,
          needsEmailVerification,
          needsProfile: false,
          settings,
          deviceLock,
          ready: true,
        }));
      } catch {
        setState((s) => ({ ...s, ready: true, uid: user.uid }));
      }
    }

    async function fetchSubOnce(subRef) {
      // Polling mode fallback for subscription
      try {
        const snap = await getDoc(subRef);
        const subData = snap.exists() ? (snap.data() || {}) : {};
        let method = "None";
        let isActive = false;
        let startDate = 0;
        let endDate = 0;
        let platform = null;
        if (subData?.manual?.startDate && subData?.manual?.endDate) {
          method = "Manual";
          startDate = Number(subData.manual.startDate || 0);
          endDate = Number(subData.manual.endDate || 0);
          platform = "manual";
          isActive = Date.now() < endDate;
        }
        // Deep comparison to prevent unnecessary re-renders
        const newSub = { method, valid: isActive, platform, startDate, endDate };
        setState((prev) => {
          const prevSubStr = JSON.stringify(prev.subscription || {});
          const newSubStr = JSON.stringify(newSub);
          if (prevSubStr === newSubStr) {
            return prev;
          }
          return { ...prev, subscription: newSub };
        });
      } catch { }
    }

    const unsubAuth = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        // clear cache for safety on sign-out
        try {
          Object.keys(localStorage).forEach((k) => {
            if (k.startsWith(CACHE_PREFIX)) localStorage.removeItem(k);
          });
        } catch { }
        if (unsubUserDoc) unsubUserDoc();
        if (unsubSubDoc) unsubSubDoc();

        setState((s) => ({
          ...s,
          ready: true,
          uid: null,
          emailVerified: false,
          needsProfile: false,
          needsEmailVerification: false,
          claims: { admin: false, managerPlus: false, manager: false },
          basePrivRole: "free",
          settings: defaultSettings,
          roleOptions: [],
          roleListAvailable: false,
          gates: { navigation: {}, details: {}, features: {} },
          subscription: { method: "None", valid: false, platform: null, startDate: 0, endDate: 0 },
          discountRules: {},
          deviceLock: null,
        }));
        lastRulesKeyRef.current = null;
        return;
      }

      const tokenRes = await user.getIdTokenResult(true); // Force refresh on initial load for fresh claims
      const claims = tokenRes?.claims || {};

      // Use claims.role if available (and not "free"), otherwise fall back to checking individual claims
      let basePrivRole;
      if (claims.role && claims.role !== "free") {
        basePrivRole = claims.role;
      } else if (claims.admin) {
        basePrivRole = "admin";
      } else if (claims.managerPlus) {
        basePrivRole = "managerPlus";
      } else if (claims.manager) {
        basePrivRole = "manager";
      } else if (claims.premium) {
        basePrivRole = "premium";
      } else if (claims.silver) {
        basePrivRole = "silver";
      } else if (claims.custom) {
        basePrivRole = "custom";
      } else {
        // Check for gold1-gold20
        for (let i = 20; i >= 1; i--) {
          if (claims[`gold${i}`]) {
            basePrivRole = `gold${i}`;
            break;
          }
        }
        if (!basePrivRole) {
          basePrivRole = "free";
        }
      }

      baseRoleRef.current = basePrivRole;

      // Save signing date to usersData collection (current timestamp)
      await saveSigningDate(db, user.uid);

      // users/{uid}
      const userRef = doc(db, "users", user.uid);

      // Update country on every page load using IP geolocation
      try {
        const ipCountry = await getCountryFromIP();
        if (ipCountry && typeof ipCountry === 'string' && ipCountry.length > 1) {
          // Fetch current country from Firestore
          const userSnap = await getDoc(userRef);
          const storedCountry = userSnap.exists() ? (userSnap.data()?.country || '') : '';
          // Update if different (case-insensitive comparison)
          if (storedCountry.toLowerCase() !== ipCountry.toLowerCase()) {
            await updateDoc(userRef, { country: ipCountry });
          }
        }
      } catch (countryErr) {
        // Silent fail - country update is not critical
      }

      // Optional: disable real-time listeners (ad blockers may block Firestore Listen channels)
      if (realtimeDisabledRef.current) {
        await fetchUserOnce(userRef, user, basePrivRole);
        // start lightweight polling every 30s
        userPollTimer = setInterval(() => fetchUserOnce(userRef, user, basePrivRole), 30000);
      } else {
        unsubUserDoc = onSnapshot(
          userRef,
          async (snap) => {
            if (!snap.exists()) {
              const needsEmailVerification = !!user.email && !user.emailVerified;
              setState((prev) => ({
                ...prev,
                uid: user.uid,
                emailVerified: !!user.email && user.emailVerified,
                needsEmailVerification,
                needsProfile: true,
                deviceLock: { locked: false, until: 0 },
                settings: defaultSettings,
                ready: true,
              }));
              return;
            }
            const d = snap.data() || {};
            // ----- Device binding & cooldown (web) -----
            const localId = getLocalDeviceId();
            const serverId = d.uniqueId || null;
            const lastUpdateRaw = d.uniqueIdUpdateDate || 0;
            const lastUpdate = typeof lastUpdateRaw === 'number' ? lastUpdateRaw : (Date.parse(String(lastUpdateRaw)) || 0);
            const lockUntil = lastUpdate > 0 ? (lastUpdate + WEB_COOLDOWN_MS) : 0;
            const privileged = ["admin", "managerPlus"].includes(basePrivRole);
            let deviceLock = { locked: false, until: 0 };

            if (!privileged) {
              try {
                if (!serverId) {
                  // First claim - but only write if profile already exists (has firstName)
                  // If no profile yet, createProfile() will write uniqueId when user completes onboarding
                  if (d.firstName && d.firstName.trim()) {
                    await setDoc(userRef, { uniqueId: localId, uniqueIdUpdateDate: Date.now() }, { merge: true });
                    wasOwnerRef.current = true;
                  }
                } else if (serverId === localId) {
                  // We are the owner
                  wasOwnerRef.current = true;
                } else {
                  // Not the owner
                  if (wasOwnerRef.current) {
                    // We lost ownership -> sign out this device
                    try { await signOut(auth); } catch { }
                  } else {
                    if (Date.now() < lockUntil) {
                      // Blocked by cooldown; show device lock screen
                      deviceLock = { locked: true, until: lockUntil, needsConfirmation: false };
                    } else {
                      // Cooldown expired but different device; show warning to confirm
                      deviceLock = { locked: false, until: 0, needsConfirmation: true };
                    }
                  }
                }
              } catch { }
            }
            const settings = {
              firstName: d.firstName || "",
              lastName: d.lastName || "",
              email: d.email || user.email || "",
              phoneNumber: d.phoneNumber || "",
              currency: normalizeCurrencyCode(d.currency),
              discount: d.discount || "free",
              personalDiscount:
                typeof d.personalDiscount === "number" ? d.personalDiscount : 0,
              country: d.country || "",
              resetDiscount: !!d.resetDiscount,
              selectedList: d.selectedList || "default",
              lists: d.lists || {},
              selectedListBoard: d.selectedListBoard || "default",
              listsBoards: d.listsBoards || {},
              canScreenshot: d.canScreenshot === true, // Screenshot permission from Firestore
            };
            settingsRef.current = settings;

            // If user has opted to reset discount on startup, zero it once per app load
            try {
              if (!resetAppliedRef.current && settings.resetDiscount) {
                resetAppliedRef.current = true;
                if (typeof settings.personalDiscount === "number" && settings.personalDiscount !== 0) {
                  await updateDoc(userRef, { personalDiscount: 0 });
                }
              }
            } catch { }

            // permissions/{basePrivRole}
            let roleOptions = [];
            let gates = { navigation: {}, details: {}, features: {} };
            let roleListAvailable = false;
            try {
              const permSnap = await getDoc(doc(db, "permissions", basePrivRole));
              if (permSnap.exists()) {
                const pd = permSnap.data() || {};
                const list = Array.isArray(pd.discountList) ? pd.discountList : [];
                roleListAvailable = Array.isArray(pd.discountList);

                // Create role options with guaranteed unique codes
                const usedCodes = new Set();
                roleOptions = sortRoleOptions(list.map((label, index) => {
                  const originalLabel = String(label || "").trim();

                  // Generate base code from label
                  let baseCode = originalLabel
                    .split(/\s+/)
                    .map((p, i) =>
                      i === 0 ? p.toLowerCase() : p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()
                    )
                    .join("")
                    .replace(/[^a-zA-Z0-9]/g, ""); // Keep numbers!

                  // Ensure unique code
                  let code = baseCode;
                  let counter = 1;
                  while (usedCodes.has(code)) {
                    code = `${baseCode}${counter}`;
                    counter++;
                  }
                  usedCodes.add(code);

                  // Improve label formatting: add space before numbers
                  const improvedLabel = originalLabel.replace(/([a-zA-Z])(\d+)/g, '$1 $2');

                  return {
                    code: code || `role_${index}`,
                    label: improvedLabel,
                  };
                }));
                gates = {
                  navigation: pd.navigation || {},
                  details: pd.details || {},
                  features: pd.features || {},
                };
              }
            } catch {
              /* ignore */
            }

            // Ensure current role visible only when discountList is absent
            if (
              !roleListAvailable &&
              settings.discount &&
              !roleOptions.some((r) => r.code === settings.discount)
            ) {
              roleOptions.push({
                code: settings.discount,
                label: settings.discount
                  .replace(/[-_]/g, " ")
                  .replace(/([a-z])([A-Z])/g, "$1 $2")
                  .replace(/\b\w/g, (m) => m.toUpperCase()),
              });
            }

            // console.log('[DEBUG] 📝 Setting state with basePrivRole:', basePrivRole);
            setState((prev) => ({
              ...prev,
              uid: user.uid,
              emailVerified: !!user.email && user.emailVerified,
              needsEmailVerification: !!user.email && !user.emailVerified,
              needsProfile: false,
              claims: {
                admin: !!claims.admin,
                managerPlus: !!claims.managerPlus,
                manager: !!claims.manager,
              },
              basePrivRole,
              settings,
              roleOptions,
              roleListAvailable,
              gates,
              deviceLock,
            }));
          },
          (err) => {
            console.warn("[users] onSnapshot error", err?.message || err);
            // Downgrade to polling and stop realtime to avoid repeated blocked requests
            try { unsubUserDoc && unsubUserDoc(); } catch { }
            realtimeDisabledRef.current = true;
            fetchUserOnce(userRef, user, basePrivRole);
            if (userPollTimer) clearInterval(userPollTimer);
            userPollTimer = setInterval(() => fetchUserOnce(userRef, user, basePrivRole), 30000);
          }
        );
      }

      // live subscriptions/{uid} → lightweight verification to compute method
      const subRef = doc(db, "subscriptions", user.uid);
      if (realtimeDisabledRef.current) {
        await fetchSubOnce(subRef);
        subPollTimer = setInterval(() => fetchSubOnce(subRef), 30000);
      } else {
        // Check once if subscription document exists before setting up listener
        const initialCheck = await getDoc(subRef);
        if (!initialCheck.exists()) {
          // No subscription document exists, not setting up listener
          // Only set initial state if we haven't initialized yet
          if (!lastSubStatusRef.current.initialized) {
            lastSubStatusRef.current = { active: false, startDate: 0, endDate: 0, method: "None", initialized: true };
            // Use deep comparison to prevent unnecessary re-renders
            const noSubState = { method: "None", valid: false, platform: null, startDate: 0, endDate: 0 };
            setState((prev) => {
              const prevSubStr = JSON.stringify(prev.subscription || {});
              const newSubStr = JSON.stringify(noSubState);
              if (prevSubStr === newSubStr) {
                return prev;
              }
              return { ...prev, subscription: noSubState };
            });
          } else {
            // Already initialized, skipping setState
          }
          // Don't set up the listener at all - just continue
        } else {
          // Document exists, set up the listener
          unsubSubDoc = onSnapshot(
            subRef,
            async (snap) => {
              try {
                // Skip verification for privileged roles
                const prevToken = await user.getIdTokenResult();
                const base = prevToken?.claims || {};
                const privileged = base.admin || base.manager || base.managerPlus;
                if (privileged) {
                  // Privileged users get valid subscription without verification
                  const privSubState = { method: "None", valid: true, platform: null, startDate: 0, endDate: 0 };
                  setState((prev) => {
                    const prevSubStr = JSON.stringify(prev.subscription || {});
                    const newSubStr = JSON.stringify(privSubState);
                    if (prevSubStr === newSubStr) {
                      return prev;
                    }
                    return { ...prev, subscription: privSubState };
                  });
                  return;
                }

                const subData = snap.data() || {};
                let method = "None";
                let isActive = false;
                let startDate = 0;
                let endDate = 0;
                let platform = null;

                // skipVerification is at subscription doc level - grants access without date checks
                const skipVerification = !!subData?.skipVerification;

                if (subData?.manual?.startDate && subData?.manual?.endDate) {
                  // Manual subscription
                  method = "Manual";
                  startDate = Number(subData.manual.startDate || 0);
                  endDate = Number(subData.manual.endDate || 0);
                  platform = "manual";
                  // If skipVerification is true, grant access regardless of dates
                  isActive = skipVerification || (Number.isFinite(endDate) && Date.now() < endDate);
                } else if (skipVerification) {
                  // User has skipVerification but no manual subscription dates
                  // Grant access without checking dates
                  isActive = true;
                }

                const subscription = { method, valid: isActive, platform, startDate, endDate };

                // Use deep comparison setState to ensure React bailout when nothing changed
                setState((prev) => {
                  const prevSubStr = JSON.stringify(prev.subscription || {});
                  const newSubStr = JSON.stringify(subscription);
                  if (prevSubStr === newSubStr) {
                    // No change - return same reference to skip re-render
                    return prev;
                  }
                  return { ...prev, subscription };
                });

                // Promotion/Demotion decisions - compare with previous state
                const prevSub = lastSubStatusRef.current;
                const prevActive = prevSub.active === true;
                const currActive = isActive === true;
                const changedActive = prevActive !== currActive || prevSub.endDate !== endDate;

                // Update the ref to track what we've processed
                lastSubStatusRef.current = { active: isActive, startDate, endDate, method, initialized: true };

                if (changedActive) {
                  const currentBaseRole = String(baseRoleRef.current || "free").toLowerCase();
                  const currentSettingsRole = String(settingsRef.current?.discount || "free").toLowerCase();
                  const privileged2 = ["admin", "manager", "managerplus"].includes(currentBaseRole);

                  // Promote: free user with active subscription (skipVerification doesn't block promotion)
                  if (isActive && currentBaseRole === "free" && currentSettingsRole === "free") {
                    // Naim has no promotion function (no promoteSubscribedUser deployed in any
                    // generation): subscriptions are manual and roles are assigned by the admin.
                    // The legacy call here targeted a function that never existed and always failed.
                    console.log('[subscription] Active subscription for free user - roles are admin-assigned on Naim');
                  } else if (isActive && currentBaseRole === "free" && currentSettingsRole !== "free") {
                    // Settings role is already set (by admin), but custom claims need updating
                    console.log('[subscription] User already has role in settings:', currentSettingsRole, '- skipping promotion');
                  } else if (!isActive && !privileged2 && !skipVerification && currentBaseRole !== "free") {
                    // Demote: not free + not privileged + not skipVerification + inactive subscription
                    try {
                      console.log('[subscription] Demoting user from subscribed to free');
                      const demote = httpsCallable(functions, "demoteUnsubscribedUser2");
                      await demote({});
                      await user.getIdToken(true);
                    } catch (e3) {
                      console.warn("demoteUnsubscribedUser failed", e3);
                    }
                  }
                }
              } catch (e) {
                console.warn("verifyUserSubscription failed", e);
                const errorSubState = { method: "None", valid: false, platform: null, startDate: 0, endDate: 0 };
                setState((prev) => {
                  const prevSubStr = JSON.stringify(prev.subscription || {});
                  const newSubStr = JSON.stringify(errorSubState);
                  if (prevSubStr === newSubStr) {
                    return prev;
                  }
                  return { ...prev, subscription: errorSubState };
                });
              }
            },
            (err) => {
              console.warn("[subscriptions] onSnapshot error", err?.message || err);
              try { unsubSubDoc && unsubSubDoc(); } catch { }
              realtimeDisabledRef.current = true;
              if (subPollTimer) clearInterval(subPollTimer);
              fetchSubOnce(subRef);
              subPollTimer = setInterval(() => fetchSubOnce(subRef), 30000);
            }
          );
        }
      }
    });

    return () => {
      unsubAuth();
      if (unsubUserDoc) unsubUserDoc();
      if (unsubSubDoc) unsubSubDoc();
      if (userPollTimer) clearInterval(userPollTimer);
      if (subPollTimer) clearInterval(subPollTimer);
    };
  }, [auth, db, functions, hasFirebase]);

  /* ------------------- React to custom-claims changes live ------------------ */
  React.useEffect(() => {
    if (!auth || !db) return;
    // Light periodic refresh to pull latest custom claims without page reload
    const interval = setInterval(() => {
      // console.log('[DEBUG] ⏰ 30s interval: refreshing token');
      const u = auth.currentUser;
      if (u) u.getIdToken(true).catch(() => { });
    }, 30000); // 30s

    // When token changes (after refresh or naturally), recompute role + gates
    const unsub = onIdTokenChanged(auth, async (u) => {
      // console.log('[DEBUG] 🎫 onIdTokenChanged fired');
      if (!u) return;
      try {
        const tok = await u.getIdTokenResult();
        const c = tok?.claims || {};

        // Use claims.role if available (and not "free"), otherwise fall back to checking individual claims
        let basePrivRole;
        if (c.role && c.role !== "free") {
          basePrivRole = c.role;
        } else if (c.admin) {
          basePrivRole = "admin";
        } else if (c.managerPlus) {
          basePrivRole = "managerPlus";
        } else if (c.manager) {
          basePrivRole = "manager";
        } else if (c.premium) {
          basePrivRole = "premium";
        } else if (c.silver) {
          basePrivRole = "silver";
        } else if (c.custom) {
          basePrivRole = "custom";
        } else {
          // Check for gold1-gold20
          for (let i = 20; i >= 1; i--) {
            if (c[`gold${i}`]) {
              basePrivRole = `gold${i}`;
              break;
            }
          }
          if (!basePrivRole) {
            basePrivRole = "free";
          }
        }

        // Check if role changed BEFORE updating ref
        const roleChanged = baseRoleRef.current !== basePrivRole;
        baseRoleRef.current = basePrivRole;

        // Refresh permission gates for the new role
        let gates = { navigation: {}, details: {}, features: {} };
        let roleOptions = [];
        let roleListAvailable = false;
        try {
          const permSnap = await getDoc(doc(db, "permissions", basePrivRole));
          if (permSnap.exists()) {
            const pd = permSnap.data() || {};
            const list = Array.isArray(pd.discountList) ? pd.discountList : [];
            roleListAvailable = Array.isArray(pd.discountList);

            // Create role options with guaranteed unique codes
            const usedCodes = new Set();
            roleOptions = sortRoleOptions(list.map((label, index) => {
              const originalLabel = String(label || "").trim();

              // Generate base code from label
              let baseCode = originalLabel
                .split(/\s+/)
                .map((p, i) =>
                  i === 0 ? p.toLowerCase() : p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()
                )
                .join("")
                .replace(/[^a-zA-Z0-9]/g, ""); // Keep numbers!

              // Ensure unique code
              let code = baseCode;
              let counter = 1;
              while (usedCodes.has(code)) {
                code = `${baseCode}${counter}`;
                counter++;
              }
              usedCodes.add(code);

              // Improve label formatting: add space before numbers
              const improvedLabel = originalLabel.replace(/([a-zA-Z])(\d+)/g, '$1 $2');

              return {
                code: code || `role_${index}`,
                label: improvedLabel,
              };
            }));

            gates = {
              navigation: pd.navigation || {},
              details: pd.details || {},
              features: pd.features || {},
            };
          }
        } catch { }

        // Only update if role actually changed to prevent infinite loops
        if (roleChanged) {
          // console.log('[DEBUG] 🎫 Role changed! Calling setState from onIdTokenChanged');
          setState((prev) => ({
            ...prev,
            claims: { admin: !!c.admin, managerPlus: !!c.managerPlus, manager: !!c.manager },
            basePrivRole,
            gates,
            roleOptions: roleOptions.length ? roleOptions : prev.roleOptions,
            roleListAvailable: roleListAvailable || prev.roleListAvailable,
          }));
        } else {
          // console.log('[DEBUG] 🎫 Role unchanged, skipping setState');
        }
      } catch { }
    });

    return () => {
      clearInterval(interval);
      unsub();
    };
  }, [auth, db]);

  /* -------------------- Fetch & cache getUserDiscount rules -------------------- */
  React.useEffect(() => {
    // console.log('[DEBUG] ⚡ getUserDiscount2 EFFECT RUNNING', {
    //   uid: state.uid,
    //   basePrivRole: state.basePrivRole,
    //   'settings.discount': state.settings.discount,
    //   method: state.subscription?.method,
    //   'state.discountRules': state.discountRules
    // });

    if (!state.uid) return;

    let cancelled = false;
    (async () => {
      // getUserDiscount2 only expects the discount tier (user's role)
      const discount = state.settings.discount || "free";
      const payload = { discount };
      // console.log('[DEBUG] 📦 Payload for getUserDiscount2:', payload);

      // Include all params in cache key for proper invalidation
      const cachePayload = {
        role: state.basePrivRole,
        discount: discount,
        country: state.settings.country || "",
        method: state.subscription?.method || "None",
      };
      const key = makeCacheKey(state.uid, cachePayload);
      // console.log('[DEBUG] 🔑 Cache key:', key, 'lastKey:', lastRulesKeyRef.current);
      let discountRules = {};

      try {
        if (lastRulesKeyRef.current === key && Object.keys(state.discountRules || {}).length > 0) {
          // unchanged inputs in this session - skip entirely, don't call setState
          // console.log('[DEBUG] ⏭️ SKIPPING - cache key unchanged:', key);
          return; // Exit early, don't call setState
        }

        const cached = readCache(key);
        if (cached && typeof cached === "object") {
          discountRules = cached;
        } else {
          const getUserDiscount2 = httpsCallable(functions, "getUserDiscount2");
          const result = await getUserDiscount2(payload);

          // handle shape: { success: { ...ranges } } OR direct object
          if (result?.data?.success && typeof result.data.success === "object") {
            discountRules = result.data.success;
          } else if (result?.data && typeof result.data === "object") {
            discountRules = result.data;
          } else {
            discountRules = {};
          }
          writeCache(key, discountRules);
        }
        lastRulesKeyRef.current = key;
      } catch (err) {
        console.warn("⚠️ getUserDiscount2 failed:", err);
        discountRules = {};
      }

      if (!cancelled) {
        // Only update state if discountRules actually changed (deep comparison)
        // This prevents infinite loops when API returns same/empty data
        setState((prev) => {
          const prevRulesStr = JSON.stringify(prev.discountRules || {});
          const newRulesStr = JSON.stringify(discountRules || {});
          if (prevRulesStr === newRulesStr && prev.ready) {
            // No change - return same reference to skip re-render
            return prev;
          }
          return { ...prev, discountRules, ready: true };
        });
      }
    })();

    return () => {
      cancelled = true;
    };
    // Re-fetch when any of these change:
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.uid,
    // state.basePrivRole is NOT in deps - we only use it for the cache key, not for fetching
    // The actual discount is based on state.settings.discount
    state.settings.discount,
    state.settings.country,
    state.subscription?.method,
    functions,
    // Note: state.discountRules is intentionally NOT in deps to avoid infinite loop
    // The effect already checks if discountRules changed before calling setState
  ]);

  /* ---------------- Subscription expiry watcher and re-verification -------- */
  React.useEffect(() => {
    const uid = state.uid;
    if (!uid || !db || !functions || !auth) return;

    const base = String(baseRoleRef.current || "free").toLowerCase();
    const privileged = ["admin", "manager", "managerplus"].includes(base);
    if (privileged) return; // skip for privileged users

    const method = state.subscription?.method || "None";
    const endDate = Number(state.subscription?.endDate || 0);
    if (!method || method === "None" || !Number.isFinite(endDate) || endDate <= 0) return;

    let timer = null;

    async function reverify() {
      try {
        const user = auth.currentUser;
        if (!user) return;

        const subSnap = await getDoc(doc(db, "subscriptions", uid));
        const subData = subSnap.exists() ? subSnap.data() : {};

        let isActive = false;
        let startDate = 0;
        let newEnd = 0;
        let platform = null;
        let newMethod = "None";

        if (subData?.manual?.startDate && subData?.manual?.endDate) {
          // Manual: honor stored endDate; demote if elapsed
          startDate = Number(subData.manual.startDate || 0);
          newEnd = Number(subData.manual.endDate || 0);
          newMethod = "Manual";
          platform = "manual";

          if (Date.now() >= newEnd) {
            const currentRole = String(settingsRef.current?.discount || "free").toLowerCase();
            const stillPriv = ["admin", "manager", "managerplus"].includes(String(baseRoleRef.current || "free").toLowerCase());
            if (currentRole !== "free" && !stillPriv) {
              try {
                const demote = httpsCallable(functions, "demoteUnsubscribedUser2");
                await demote({});
                await user.getIdToken(true);
              } catch { }
            }
            isActive = false;
          } else {
            // still active within window
            isActive = true;
          }
        } else {
          // For non-manual subscriptions, verify via CF
          try {
            const verifySubscription = httpsCallable(functions, "verifySubscription");
            const result = await verifySubscription({ uid });
            const d = result?.data || {};
            newMethod = d.method || "None";
            isActive = !!d.isActive;
            platform = d.platform || (d.method === "Manual" ? "manual" : null);
            startDate = Number(d.startDate || 0);
            newEnd = Number(d.endDate || 0);
          } catch { }

          if (!isActive) {
            const currentRole = String(settingsRef.current?.discount || "free").toLowerCase();
            const stillPriv = ["admin", "manager", "managerplus"].includes(String(baseRoleRef.current || "free").toLowerCase());
            if (currentRole !== "free" && !stillPriv) {
              try {
                const demote = httpsCallable(functions, "demoteUnsubscribedUser2");
                await demote({});
                await user.getIdToken(true);
              } catch { }
            }
          }
        }

        // Deep comparison to prevent unnecessary re-renders
        const newSub = { method: newMethod, valid: isActive, platform, startDate, endDate: newEnd };
        setState((prev) => {
          const prevSubStr = JSON.stringify(prev.subscription || {});
          const newSubStr = JSON.stringify(newSub);
          if (prevSubStr === newSubStr) {
            return prev;
          }
          return { ...prev, subscription: newSub };
        });

        // If still active and we have a future end date, schedule next check
        if (isActive && newEnd && newEnd > Date.now()) {
          const ms = Math.max(1000, newEnd - Date.now() + 1000);
          timer = setTimeout(reverify, ms);
        }
      } catch { }
    }

    const now = Date.now();
    if (endDate <= now) {
      // Expired: reverify immediately
      reverify();
    } else {
      // Schedule at expiry
      const ms = Math.max(1000, endDate - now + 1000);
      timer = setTimeout(reverify, ms);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [state.uid, state.subscription?.method, state.subscription?.endDate, db, functions, auth]);

  /* --------------------------- Pricing (memoized) --------------------------- */
  // Use JSON.stringify in dependency to ensure stable reference comparison
  // This prevents re-creating pricing functions when discountRules object reference
  // changes but content remains the same
  const discountRulesKey = JSON.stringify(state.discountRules || {});
  const pricing = React.useMemo(() => {
    const rules = state.discountRules || {};
    const personal = Number(state.settings.personalDiscount) || 0;

    return {
      computePriceUSD(baseUSD) {
        const b = Number(baseUSD) || 0;
        const rolePct = percentForPrice(rules, b);
        const afterRole = b * (1 - rolePct / 100);
        const afterPersonal = afterRole * (1 - personal / 100);
        // round UP to 2 decimals
        const final = Math.ceil(afterPersonal * 100) / 100;
        return final;
      },
      explain(baseUSD) {
        const b = Number(baseUSD) || 0;
        const rolePct = percentForPrice(rules, b);
        const personalPct = personal;
        return { rolePct, personalPct };
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discountRulesKey, state.settings.personalDiscount]);

  /* ------------------------------ Refresh helper --------------------------- */
  const createProfile = React.useCallback(
    async ({ firstName, lastName, email, phoneNumber }) => {
      if (!auth || !db) throw new Error("Auth not ready");
      const user = auth.currentUser;
      if (!user) throw new Error("No authenticated user");

      // Ensure we have at least email or phoneNumber for Cloud Function compatibility
      const userEmail = String(email || user.email || "").trim();
      const userPhone = String(phoneNumber || user.phoneNumber || "").trim();

      if (!userEmail && !userPhone) {
        throw new Error("At least an email or phone number is required");
      }

      const payload = {
        firstName: String(firstName || "").trim(),
        lastName: String(lastName || "").trim(),
        email: userEmail,
        phoneNumber: userPhone,
        currency: "USD",
        discount: "free",
        personalDiscount: 0,
        country: "",
        resetDiscount: true,
        selectedList: "default",
        uniqueId: getLocalDeviceId(),
        uniqueIdUpdateDate: Date.now(),
        lists: {
          default: { listName: "Default" },
        },
      };

      const userRef = doc(db, "users", user.uid);
      await setDoc(userRef, payload);
      await setDoc(doc(db, "buyLists", user.uid), { default: {} }, { merge: true });
      try {
        await user.updateProfile({ displayName: `${payload.firstName} ${payload.lastName}`.trim() });
      } catch { }
      setState((s) => ({
        ...s,
        needsProfile: false,
        settings: { ...s.settings, ...payload },
        deviceLock: { locked: false, until: 0 },
      }));
    },
    [auth, db]
  );

  const resendEmailVerification = React.useCallback(async () => {
    if (!auth) throw new Error("Auth not ready");
    const user = auth.currentUser;
    if (!user || !user.email) throw new Error("No email user found");
    await user.sendEmailVerification();
  }, [auth]);

  const refreshUser = React.useCallback(async () => {
    if (!auth) return;
    const user = auth.currentUser;
    if (!user) return;
    await user.reload();
    setState((s) => ({
      ...s,
      emailVerified: !!user.email && user.emailVerified,
      needsEmailVerification: !!user.email && !user.emailVerified,
    }));
  }, [auth]);

  const signOutUser = React.useCallback(async () => {
    if (!auth) return;
    await signOut(auth);
  }, [auth]);

  const refreshUserSettings = React.useCallback(async () => {
    if (!state.uid) return;
    const snap = await getDoc(doc(db, "users", state.uid));
    if (snap.exists()) {
      const d = snap.data() || {};
      setState((s) => ({ ...s, settings: { ...s.settings, ...d } }));
    }
  }, [db, state.uid]);

  const forceSignIn = React.useCallback(async () => {
    if (!auth || !db || !state.uid) return;
    const localId = getLocalDeviceId();
    const userRef = doc(db, "users", state.uid);
    try {
      await setDoc(userRef, { uniqueId: localId, uniqueIdUpdateDate: Date.now() }, { merge: true });
      wasOwnerRef.current = true;
      setState((s) => ({ ...s, deviceLock: { locked: false, until: 0, needsConfirmation: false } }));
    } catch (err) {
      console.error("[forceSignIn] Failed to claim device:", err);
      throw err;
    }
  }, [auth, db, state.uid]);

  /* ------------------------------- Context value --------------------------- */
  const value = React.useMemo(
    () => ({
      ...state,
      pricing,
      refreshUserSettings,
      createProfile,
      resendEmailVerification,
      refreshUser,
      signOut: signOutUser,
      forceSignIn,
    }),
    [state, pricing, refreshUserSettings, createProfile, resendEmailVerification, refreshUser, signOutUser, forceSignIn]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

/* --------------------------------------------------------------------------
   Post-expiry re-verification of subscription status
   - When endDate passes, re-verify via CF.
   - For manual subs whose endDate elapsed, call demoteUnsubscribedUser.
---------------------------------------------------------------------------- */
export function __attachSubExpiryWatcherForTesting() { }

// Attach within same module scope for access to refs via closure
// We rely on React effects inside UserProvider to set state. Below, we add
// a secondary effect that schedules a check when the subscription endDate
// is reached, so changes apply without Firestore doc updates or page reloads.
(function addExpiryEffectToUserProviderPrototype() {
  // Monkey-patch by creating a hook the provider will run implicitly.
  // Since we canâ€™t rewrite the component structure broadly, we replicate
  // the effect pattern here and invoke it by importing this module once.
})();

